import type { Locale } from "./locale"

/**
 * Words the shared page chrome prints (skip link, breadcrumb, answer box, source line, navigation,
 * footer, product-page headings). Page copy is not here: each translated page has its own content
 * file under `brand/content/zh/`. Chinese terms follow docs/design/landing/copy-zh.md.
 */
export interface Ui {
  skipToMain: string
  breadcrumb: string
  home: string
  shortAnswer: string
  sourcePrefix: string
  financialYear: (year: string) => string
  checkedOn: (date: string) => string
  lastReviewed: string
  readNext: string
  /** Guide and calculator pages (GuidePage). */
  guides: string
  onThisPage: string
  commonQuestions: string
  ratesLine: (year: string) => string
  more: string
  less: string
  /** "2026-10-27" -> the date as the language writes it. */
  longDate: (iso: string) => string
  guideDisclaimer: string
  guideCta: { title: string; description: string; ctaLabel: string; ctaHref: string }
  mainNav: string
  menu: string
  signIn: string
  /** Google and Apple sign-in on the login page (T1414). */
  signInProviders: {
    groupLabel: string
    continueWithGoogle: string
    continueWithApple: string
    /** Divider between the provider buttons and the email form. */
    or: string
    errors: { cancelled: string; failed: string; emailExists: string }
  }
  startTrial: string
  dashboard: string
  account: {
    menu: (name: string) => string
    profile: string
    business: string
    settings: string
    help: string
    signOut: string
  }
  footerNav: string
  legalNav: string
  disclaimer: string
  keyFacts: string
  whatIsIncluded: string
  whatItDoesNot: string
  whoItSuits: string
  whatIsInPlan: string
  draftPrices: string
  seeFullPricing: string
  plans: { free: string; logbook: string; pro: string }
  unlimited: string
  notIncluded: string
  perMonth: (n: number) => string
  eachExtra: (price: string) => string
  planRows: { trips: string; invoices: string; aiReads: string; vehicles: string }
}

export const UI: Record<Locale, Ui> = {
  en: {
    skipToMain: "Skip to main content",
    breadcrumb: "Breadcrumb",
    home: "Home",
    shortAnswer: "Short answer",
    sourcePrefix: "Source:",
    financialYear: (y) => `FY ${y}`,
    checkedOn: (d) => `checked ${d}`,
    lastReviewed: "Last reviewed",
    readNext: "Read next",
    guides: "Guides",
    onThisPage: "On this page",
    commonQuestions: "Common questions",
    ratesLine: (y) => `Rates: ATO, FY ${y}`,
    more: "More",
    less: "Less",
    longDate: (iso) =>
      new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-AU", {
        day: "numeric",
        month: "long",
        year: "numeric",
        timeZone: "UTC",
      }),
    guideDisclaimer:
      "Estimates only. This page gives general information, not tax advice; check your situation with the ATO or a registered tax agent.",
    guideCta: {
      title: "Track the kilometres automatically",
      description: "Record trips on your phone and export a report for your claim.",
      ctaLabel: "Start free trial",
      ctaHref: "/login",
    },
    mainNav: "Main",
    menu: "Menu",
    signIn: "Sign in",
    signInProviders: {
      groupLabel: "Sign in with Google or Apple",
      continueWithGoogle: "Continue with Google",
      continueWithApple: "Continue with Apple",
      or: "or",
      errors: {
        cancelled: "Sign-in was cancelled. You can try again, or use an email code instead.",
        failed:
          "We couldn't sign you in with that provider. Try again, or use an email code instead.",
        emailExists:
          "An account with this email already exists and uses a different sign-in method. Sign in the way you did before, for example with an email code.",
      },
    },
    startTrial: "Start free trial",
    dashboard: "Dashboard",
    account: {
      menu: (name) => `${name}, account menu`,
      profile: "Profile",
      business: "Business",
      settings: "Settings",
      help: "Help & feedback",
      signOut: "Sign out",
    },
    footerNav: "Footer",
    legalNav: "Legal",
    disclaimer:
      "Estimates only. This site gives general information, not tax advice; check your situation with the ATO or a registered tax agent.",
    keyFacts: "Key facts",
    whatIsIncluded: "What is included",
    whatItDoesNot: "What it does not do",
    whoItSuits: "Who it suits",
    whatIsInPlan: "What is in each plan",
    draftPrices: "Draft prices",
    seeFullPricing: "See full pricing",
    plans: { free: "Free", logbook: "Logbook", pro: "Pro" },
    unlimited: "Unlimited",
    notIncluded: "Not included",
    perMonth: (n) => `${n} a month`,
    eachExtra: (p) => `+${p} each extra`,
    planRows: {
      trips: "GPS trips",
      invoices: "Tax invoices",
      aiReads: "AI receipt reads",
      vehicles: "Vehicles",
    },
  },
  zh: {
    skipToMain: "跳到主要内容",
    breadcrumb: "面包屑导航",
    home: "首页",
    shortAnswer: "简短回答",
    sourcePrefix: "来源：",
    financialYear: (y) => `${y} 财年`,
    checkedOn: (d) => `核对于 ${d}`,
    lastReviewed: "最近复核",
    readNext: "接着读",
    guides: "指南（英文）",
    onThisPage: "本页目录",
    commonQuestions: "常见问题",
    ratesLine: (y) => `费率：ATO，${y} 财年`,
    more: "展开",
    less: "收起",
    longDate: (iso) => {
      const [y, m, d] = iso.split("-").map(Number)
      return `${y}年${m}月${d}日`
    },
    guideDisclaimer:
      "仅为估算。本页提供一般性信息，不构成税务建议；请向 ATO 或注册税务代理核实你的具体情况。",
    guideCta: {
      title: "自动记录行驶公里数",
      description: "用手机记录行程，导出报告用于报税申报。",
      ctaLabel: "开始免费试用",
      ctaHref: "/login",
    },
    mainNav: "主导航",
    menu: "菜单",
    signIn: "登录",
    signInProviders: {
      groupLabel: "使用 Google 或 Apple 登录",
      continueWithGoogle: "使用 Google 继续",
      continueWithApple: "使用 Apple 继续",
      or: "或",
      errors: {
        cancelled: "已取消登录。你可以重试，或改用邮箱验证码登录。",
        failed: "暂时无法通过该方式登录。请重试，或改用邮箱验证码登录。",
        emailExists:
          "这个邮箱已有账号，且使用的是另一种登录方式。请用之前的方式登录，例如邮箱验证码。",
      },
    },
    startTrial: "开始免费试用",
    dashboard: "控制台",
    account: {
      menu: (name) => `${name}，账户菜单`,
      profile: "个人资料",
      business: "业务信息",
      settings: "设置",
      help: "帮助与反馈",
      signOut: "退出登录",
    },
    footerNav: "页脚导航",
    legalNav: "法律信息",
    disclaimer:
      "仅为估算。本网站提供一般性信息，不构成税务建议；请向 ATO 或注册税务代理核实你的具体情况。法律文件以英文版为准。",
    keyFacts: "要点",
    whatIsIncluded: "包含什么",
    whatItDoesNot: "不包含什么",
    whoItSuits: "适合谁",
    whatIsInPlan: "各套餐包含什么",
    draftPrices: "草案价格",
    seeFullPricing: "查看完整价格",
    plans: { free: "免费版", logbook: "Logbook", pro: "Pro" },
    unlimited: "不限",
    notIncluded: "不包含",
    perMonth: (n) => `每月 ${n} 个`,
    eachExtra: (p) => `额外每辆 +${p}`,
    planRows: {
      trips: "GPS 行程",
      invoices: "Tax Invoice",
      aiReads: "AI 读取收据",
      vehicles: "车辆",
    },
  },
}
